import { useContext } from "react";
import { useSelector } from "react-redux";

import { CheckIcon } from "../../../client/assets/icons/common/CheckIcon";
import { RightArrowIcon } from "../../../client/assets/icons/common/RightArrowIcon";
import { BaseBenefitsList } from "../../../components/BaseComponents/BaseBenefitsList";
import { BaseCompanyLogo } from "../../../components/BaseComponents/BaseCompanyLogo";
import BaseCompanyName from "../../../components/BaseComponents/BaseCompanyName";
import { BaseFeaturedCompanyHeader } from "../../../components/BaseComponents/BaseFeaturedCompanyHeader";
import BaseGridRatingHorizontal from "../../../components/BaseComponents/BaseGridRatingHorizontal";
import { BaseLicensingNumber } from "../../../components/BaseComponents/BaseLicensingNumber";
import BaseLinkWrapper from "../../../components/BaseComponents/BaseLinkWrapper";
import BaseProductDescription from "../../../components/BaseComponents/BaseProductDescription";
import BaseServiceFlag from "../../../components/BaseComponents/BaseServiceFlag";
import { BaseSocialProof } from "../../../components/BaseComponents/BaseSocialProof";
import { BaseVisitLink } from "../../../components/BaseComponents/BaseVisitLink";
import { CompanyContext } from "../../../components/CompanyList/CompanyContext";
import { useDeemphasizedCompany } from "../../../hooks/useDeemphasizedCompany";
import { getAwardText } from "../../../selectors";
import { EditableFields } from "./EditableFields";
import { PhoneIconLink } from "./PhoneIconLink";
import { getStyles } from "./styles";
import { DEFAULT_VISIT_LINK_TEXT, ThemeContext } from "./ThemeContext";

export const MortgageRatesCompany = () => {
	const { company, companyIndex, isFeaturedCompany } =
		useContext(CompanyContext);
	const awardText = useSelector((state) => getAwardText(state, { company }));
	const { isDeemphasizedCompany, compareCompanyName, deemphasizedLinkParams } =
		useDeemphasizedCompany();
	const styles = getStyles({ isDeemphasizedCompany });
	const { visitLinkText: themeVisitLinkText = DEFAULT_VISIT_LINK_TEXT } =
		useContext(ThemeContext);
	const visitLinkText = compareCompanyName
		? `Compare With ${compareCompanyName}`
		: themeVisitLinkText;

	return (
		<div
			className={`tw:block tw:rounded-[12px] tw:bg-white tw:shadow-[0px_0px_2px_0px_rgba(24,39,75,0.18),0px_6px_12px_-6px_rgba(24,39,75,0.12),0px_8px_24px_-4px_rgba(24,39,75,0.08)] tw:transition tw:hover:shadow-[0px_8px_32px_0px_rgba(0,0,0,0.08),0px_2px_20px_0px_rgba(0,0,0,0.04)] ${
				isFeaturedCompany && !isDeemphasizedCompany
					? "tw:ring-2 tw:ring-[#0756C0]"
					: ""
			}`}
		>
			{!isDeemphasizedCompany && (
				<BaseFeaturedCompanyHeader {...styles.featuredCompanyHeader} />
			)}
			<div className="tw:relative">
				<BaseServiceFlag classNames={styles.serviceFlag} />
				<div className="tw:mx-auto tw:flex tw:max-w-[400px] tw:flex-col tw:gap-4 tw:p-4 tw:md:max-w-[500px] tw:lg:max-w-none tw:lg:flex-row tw:lg:gap-0 tw:lg:p-6">
					<BaseLinkWrapper
						extraQueryParams={deemphasizedLinkParams}
						className={`tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:pb-0 tw:lg:w-[204px] tw:lg:py-0 tw:lg:pr-3 tw:xl:w-[284px] ${
							awardText ? "tw:pt-11" : "tw:pt-0"
						} ${isDeemphasizedCompany ? "tw:grayscale" : ""}`}
					>
						<div className="tw:flex tw:w-40 tw:flex-col tw:items-center tw:justify-center tw:gap-2">
							<BaseCompanyLogo
								classNames={styles.companyLogo}
								width={160}
								height={160}
							/>
							<BaseLicensingNumber
								className={`tw:text-sm tw:leading-6 tw:font-medium tw:opacity-60 ${isDeemphasizedCompany ? "tw:text-[#949597]" : "tw:text-[#212121]"}`}
								displayName="NMLS #"
								field={"nmlsNumber"}
							/>
							<BaseGridRatingHorizontal
								classNames={styles.rating}
								ratingLabelOverride={
									isDeemphasizedCompany ? "Not Recommended" : undefined
								}
							/>
						</div>
					</BaseLinkWrapper>

					<BaseLinkWrapper
						extraQueryParams={deemphasizedLinkParams}
						className={`tw:flex tw:grow tw:flex-col tw:lg:max-w-none tw:lg:px-3 ${isDeemphasizedCompany ? "tw:grayscale" : ""}`}
					>
						<div className="tw:flex tw:w-full tw:flex-col">
							<BaseCompanyName
								className={`tw:text-2xl tw:leading-8 tw:font-bold tw:tracking-[-0.24px] ${isDeemphasizedCompany ? "tw:text-[#949597]" : "tw:text-[#212121]"}`}
							/>
							<BaseProductDescription
								className={`tw:mt-2 tw:text-xl tw:leading-6 tw:font-medium tw:lg:text-2xl tw:lg:leading-8 ${isDeemphasizedCompany ? "tw:text-[#949597]" : "tw:text-[#212121]"}`}
							/>
							<EditableFields />
							<BaseBenefitsList
								{...styles.benefitsList}
								icon={
									<CheckIcon
										className={`tw:text-xl tw:sm:mt-px ${isDeemphasizedCompany ? "tw:text-[#949597]" : "tw:text-[#0756C0]"}`}
									/>
								}
							/>
						</div>
					</BaseLinkWrapper>

					<div className="tw:mt-2 tw:flex tw:shrink-0 tw:lg:mt-0 tw:lg:w-[212px] tw:lg:pl-3">
						<div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:justify-center tw:gap-4">
							<div className="tw:flex tw:w-full tw:flex-wrap tw:gap-3 tw:lg:gap-4">
								{!isDeemphasizedCompany && <PhoneIconLink />}
								<BaseLinkWrapper
									extraQueryParams={deemphasizedLinkParams}
									className="tw:flex tw:flex-1 tw:lg:w-full"
								>
									<BaseVisitLink
										{...styles.visitLink}
										icon={<RightArrowIcon />}
									>
										{visitLinkText}
									</BaseVisitLink>
								</BaseLinkWrapper>
							</div>
							{companyIndex === 0 && !isDeemphasizedCompany && (
								<>
									<svg
										style={{ width: 0, height: 0, position: "absolute" }}
										aria-hidden="true"
										focusable="false"
									>
										<linearGradient
											x1="16"
											x2="16"
											y1="0"
											y2="32"
											gradientUnits="userSpaceOnUse"
											id="MortgageRatesCompanyGradient"
										>
											<stop stopColor="#0756C0" />
											<stop offset="1" stopColor="#2F97F4" />
										</linearGradient>
									</svg>
									<BaseSocialProof
										contentIconFill="url(#MortgageRatesCompanyGradient)"
										{...styles.socialProof}
									/>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
